import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import * as s from "./FloatListStyled";
import * as mediaQuery from "components/MediaQuery";
import WebAppLink from "components/blocks/link/WebAppLink";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  DashboardFilterAlert,
  MapSearchData,
} from "api/interfaces/dashboardInterface.interface";
import { TbChevronsUp, TbChevronsDown } from "react-icons/tb";

import { motion, AnimatePresence } from "framer-motion";
import { AlertViewInfo } from "api/interfaces/alertInterface.interface";
import { useMediaQuery } from "react-responsive";
import SPECTRUM_WHITE from "assets/images/spectrum_white.png";
import SPECTRUM_BLACK from "assets/images/spectrum_black.png";
import VMAX_WHITE from "assets/images/VMAX_white.png";
import VMAX_BLACK from "assets/images/VMAX_black.png";
import { setAlerts } from "redux/reducers/map/mapFilter";
import COVA from "assets/icons/cova-logo.svg";

import {
  enumRecorderStatus,
  getMarker,
  getTopAlertStatus,
  getTopAlertStatusString,
  RecorderStatus,
} from "../../utils";
import { convertType } from "utils/functions";
import { RecorderType } from "utils/RecorderUtil";
import { visibleMapSearchDataSelector } from "redux/stomp/stompMapSlice";

type Props = {
  innerSize: { [key: string]: number };

  onClickRecorder: (id: string) => void;
  onSelectRecorder: (rec: MapSearchData) => void;
  isViewRecorderList: boolean;
  selectedAlert: AlertViewInfo | null;
  onHoverSystem: (rec: MapSearchData | null) => void;
  cloudStatus: boolean;
};

type AlertType = "systemAlerts" | "storageAlerts" | "videoAlerts";
type TypeStatus = Record<AlertType, boolean>;
type AlertTypeSummary = Record<AlertType, number>;

const changeTypeString = (key: AlertType) => {
  switch (key) {
    case "systemAlerts":
      return "systemAlerts";
    case "storageAlerts":
      return "diskAlerts";
    case "videoAlerts":
      return "videoAlerts";
    default:
      return key;
  }
};
const alertItems = [
  {
    name: "System",
    key: "systemAlerts",
  },
  {
    name: "Disk",
    key: "storageAlerts",
  },
  {
    name: "Video",
    key: "videoAlerts",
  },
];

const changeType = (type: RecorderType, theme: "light" | "dark") => {
  if (theme === "light") {
    switch (type) {
      case "VMAX-IP":
        return VMAX_BLACK;
      case "VMAX-A1":
        return VMAX_BLACK;
      case "SPECTRUM":
        return SPECTRUM_BLACK;
      case "COVA":
        return COVA;
      default:
        return "";
    }
  } else {
    switch (type) {
      case "VMAX-IP":
        return VMAX_WHITE;
      case "VMAX-A1":
        return VMAX_WHITE;
      case "SPECTRUM":
        return SPECTRUM_WHITE;
      case "COVA":
        return COVA;
      default:
        return "";
    }
  }
};

// alert type 안에 있는 모든 요소들 토글처리
const toggleAlertTypeStatus = (status: { [key: string]: boolean }) => {
  return Object.fromEntries(
    Object.entries(status).map(([key, value]) => [key, !value])
  );
};
export const filteringTopAlert = (
  recorderList: MapSearchData[]
): RecorderStatus => {
  const isCritical = recorderList.some((recorder) => {
    return getTopAlertStatus(recorder) === enumRecorderStatus.CRITICAL;
  });
  if (isCritical) {
    return "CRITICAL";
  }

  const isAlert = recorderList.some((recorder) => {
    return getTopAlertStatus(recorder) === enumRecorderStatus.ALERT;
  });
  if (isAlert) {
    return "ALERT";
  }

  if (isAlert) {
    return "WARNING";
  }
  return "HEALTHY";
};

const FloatList = (props: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery({ maxWidth: mediaQuery.mobileMax });
  const [isDetail, setIsDetail] = useState<boolean>(isMobile ? false : true);
  const [topAlert, setTopAlert] = useState<string>("HEALTHY");
  const [alertTypeStatus, setAlertTypeStatus] = useState<TypeStatus>({
    systemAlerts: false,
    storageAlerts: false,
    videoAlerts: false,
  });

  const themeMode = useAppSelector((state) => state.theme.theme);
  const dashboardData: MapSearchData[] = useAppSelector(
    visibleMapSearchDataSelector
  ).filter((data) => data.location);

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const alertTypeFilter: DashboardFilterAlert = useAppSelector(
    (state) => state.mapFilter.filters.alerts
  );

  // // 총 AlertCount
  const totalAlerts: number = useMemo(() => {
    if (!dashboardData || dashboardData.length === 0) {
      return 0;
    }

    return dashboardData.reduce((acc, cur) => {
      const alertLength = cur.alerts?.length ?? 0;
      const criticalLength = cur.critical?.length ?? 0;

      return (acc += alertLength + criticalLength);
    }, 0);
  }, [dashboardData]);

  const alertTypeSummary: AlertTypeSummary = useMemo(() => {
    const init: AlertTypeSummary = {
      systemAlerts: 0,
      storageAlerts: 0,
      videoAlerts: 0,
    };

    if (!dashboardData || dashboardData.length === 0) {
      return init;
    }

    // category → 합산 키 매핑
    const CATEGORY_TO_KEY: Partial<Record<string, AlertType>> = {
      SYSTEM: "systemAlerts",
      DISK: "storageAlerts",
      STORAGE: "storageAlerts",
      VIDEO: "videoAlerts",
    };

    const addByCategory = (
      acc: AlertTypeSummary,
      category?: string
    ): AlertTypeSummary => {
      if (!category) {
        return acc;
      }
      const key =
        CATEGORY_TO_KEY[category.toUpperCase()] ??
        // 예외 케이스(부분일치) 방어
        (category.toLowerCase().includes("system")
          ? "systemAlerts"
          : category.toLowerCase().includes("video")
          ? "videoAlerts"
          : category.toLowerCase().includes("disk") ||
            category.toLowerCase().includes("storage")
          ? "storageAlerts"
          : undefined);

      if (key) {
        acc[key] += 1;
      }
      return acc;
    };

    return dashboardData.reduce(
      (acc, cur) => {
        cur.alerts?.forEach((a) => addByCategory(acc, a.category));
        cur.critical?.forEach((a) => addByCategory(acc, a.category));
        return acc;
      },
      { ...init }
    );
  }, [dashboardData]);

  const onClickType = useCallback(
    (key: AlertType) => {
      dispatch(
        setAlerts({
          ...alertTypeFilter,
          [changeTypeString(key)]: toggleAlertTypeStatus(
            alertTypeFilter[changeTypeString(key)]
          ),
        })
      );
    },
    [alertTypeFilter, alertTypeStatus]
  );

  useEffect(() => {
    setTopAlert(filteringTopAlert(dashboardData));
  }, [dashboardData]);

  useEffect(() => {
    const updateAlertTypeStatus = (filterAlert: DashboardFilterAlert) => {
      const newAlertTypeStatus: TypeStatus = {
        systemAlerts: Object.values(filterAlert.systemAlerts).every(Boolean),
        storageAlerts: Object.values(filterAlert.diskAlerts).every(Boolean),
        videoAlerts: Object.values(filterAlert.videoAlerts).every(Boolean),
      };

      return newAlertTypeStatus;
    };
    setAlertTypeStatus(updateAlertTypeStatus(alertTypeFilter));
  }, [alertTypeFilter]);

  return (
    <s.FloatWrapper>
      {selectedAccount.isEnabled && (
        <mediaQuery.Default>
          <s.TopInfo>
            <s.AlertStatus status={topAlert}>
              <h5>{totalAlerts} Alerts</h5>
              <span className="recorder-count">
                <strong>{dashboardData.length || 0}</strong> SYS
              </span>
            </s.AlertStatus>
            <s.AlertContents>
              <s.AlertInfo>
                {alertItems.map((item) => (
                  <s.AlertInfoItem
                    key={item.key}
                    isOnType={alertTypeStatus[item.key as AlertType]}
                    onClick={() => onClickType(item.key as AlertType)}
                    data-testid="dashboard-map-float-alert-info-item-click-type"
                  >
                    <p className="title">{item.name}</p>
                    <p className="count">
                      {alertTypeSummary[item.key as AlertType]}
                    </p>
                  </s.AlertInfoItem>
                ))}
              </s.AlertInfo>
            </s.AlertContents>
          </s.TopInfo>
        </mediaQuery.Default>
      )}

      <mediaQuery.Mobile>
        <s.Button
          onClick={() => setIsDetail(!isDetail)}
          data-testid="dashboard-map-float-button-set-is-detail"
        >
          {isDetail ? <TbChevronsDown size={20} /> : <TbChevronsUp size={20} />}
        </s.Button>
      </mediaQuery.Mobile>

      <mediaQuery.Default>
        {dashboardData.length > 0 && (
          <s.Button
            onClick={() => setIsDetail(!isDetail)}
            data-testid="dashboard-map-float-button-set-is-detail-2"
          >
            {isDetail ? (
              <TbChevronsUp size={25} />
            ) : (
              <TbChevronsDown size={25} />
            )}
          </s.Button>
        )}
      </mediaQuery.Default>
      <AnimatePresence>
        {isDetail && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {selectedAccount.isEnabled && (
              <mediaQuery.Mobile>
                <s.TopInfo>
                  <s.AlertStatus status={topAlert}>
                    <h5>{totalAlerts} Alerts</h5>/
                    <span className="recorder-count">
                      <strong>{dashboardData.length || 0}</strong> Rec(s)
                    </span>
                  </s.AlertStatus>
                  <s.AlertContents>
                    <s.AlertInfo>
                      {alertItems.map((item) => (
                        <s.AlertInfoItem
                          key={item.key}
                          isOnType={alertTypeStatus[item.key as AlertType]}
                        >
                          <p className="title">{item.name}</p>
                          <p className="count">
                            {alertTypeSummary[item.key as AlertType]}
                          </p>
                        </s.AlertInfoItem>
                      ))}
                    </s.AlertInfo>
                  </s.AlertContents>
                </s.TopInfo>
              </mediaQuery.Mobile>
            )}

            <s.AlertDetail>
              {dashboardData.map((rec: MapSearchData, idx: number) => {
                let status = getTopAlertStatusString(rec);

                return (
                  // Array.from({ length: 100 }, (_, i) => (
                  <s.RecorderItem
                    key={`recorder-item-${idx}`}
                    status={status}
                    onClick={() => {
                      props.onClickRecorder(rec.recorderId);
                      props.onSelectRecorder(rec);
                    }}
                    onMouseOver={() => props.onHoverSystem(rec)}
                    onMouseLeave={() => props.onHoverSystem(null)}
                    data-testid="dashboard-map-float-recorder-item-click-recorder"
                  >
                    <div className="top">
                      <div className="alert-status-icon">
                        <div className="alert-marker">
                          <img src={getMarker(status)} alt={status} />
                        </div>
                      </div>

                      <div className="recorder-info">
                        {convertType({
                          type: rec.type,
                          displayType: rec.displayType,
                        }) !== "UNKNOWN" && (
                          <div className="alert-type-icon">
                            <img
                              src={changeType(
                                convertType({
                                  type: rec.type,
                                  displayType: rec.displayType,
                                }),
                                themeMode
                              )}
                              alt={`${rec.type}-icon`}
                            />
                          </div>
                        )}

                        <div className="title-wrapper">
                          <span className="title">
                            {rec.type === "Spectrum"
                              ? rec.mergedSystemName
                              : rec.name}
                          </span>
                        </div>
                        {rec.type === "Spectrum" && (
                          <span className="mergedSystemName">{rec.name}</span>
                        )}

                        <span className="location">{rec.locationAddress}</span>
                      </div>
                    </div>
                    <div
                      className="web-app"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WebAppLink
                        {...rec}
                        accountId={selectedAccount.accountId}
                        cloudStatus={props.cloudStatus}
                      />
                    </div>
                  </s.RecorderItem>
                  // ))
                );
              })}
            </s.AlertDetail>
          </motion.div>
        )}
      </AnimatePresence>
    </s.FloatWrapper>
  );
};

export default FloatList;
